/**
 * Assessment Storage
 *
 * Manages localStorage persistence for assessment blueprints, attempts, and reports.
 *
 * MOVED from src/utils/assessmentStorage.ts (Phase I)
 */

import {
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentReport,
  AssessmentHistoryEntry,
} from '../types'
import { GroupId } from '../../../config/systemConfig'
import { STORAGE_KEY, ASSESSMENT_COOLDOWN } from '../../../config/constants'
import { debug, error as logError } from '../../../utils/logger'
import { safeStorageWrite } from '../../../utils/storageQuota'

const BLUEPRINTS_KEY = `${STORAGE_KEY}_assessment_blueprints`
const ATTEMPTS_KEY = `${STORAGE_KEY}_assessment_attempts`
const REPORTS_KEY = `${STORAGE_KEY}_assessment_reports`

export function saveBlueprint(blueprint: AssessmentBlueprint): void {
  try {
    const stored = localStorage.getItem(BLUEPRINTS_KEY)
    const blueprints: Record<string, AssessmentBlueprint> = stored
      ? JSON.parse(stored)
      : {}
    blueprints[blueprint.id] = blueprint
    safeStorageWrite(BLUEPRINTS_KEY, JSON.stringify(blueprints))
    debug(`[AssessmentStorage] Saved blueprint: ${blueprint.id}`)
  } catch (error) {
    logError('Failed to save blueprint:', error)
  }
}

export function getBlueprint(id: string): AssessmentBlueprint | null {
  try {
    const stored = localStorage.getItem(BLUEPRINTS_KEY)
    if (!stored) return null
    const blueprints: Record<string, AssessmentBlueprint> = JSON.parse(stored)
    return blueprints[id] || null
  } catch (error) {
    logError('Failed to load blueprint:', error)
    return null
  }
}

export function getAllBlueprints(): AssessmentBlueprint[] {
  try {
    const stored = localStorage.getItem(BLUEPRINTS_KEY)
    if (!stored) return []
    const blueprints: Record<string, AssessmentBlueprint> = JSON.parse(stored)
    return Object.values(blueprints).sort((a, b) => b.createdAt - a.createdAt)
  } catch (error) {
    logError('Failed to load blueprints:', error)
    return []
  }
}

export function saveAttempt(attempt: AssessmentAttempt): void {
  try {
    const stored = localStorage.getItem(ATTEMPTS_KEY)
    const attempts: Record<string, AssessmentAttempt> = stored
      ? JSON.parse(stored)
      : {}
    attempts[attempt.id] = attempt
    safeStorageWrite(ATTEMPTS_KEY, JSON.stringify(attempts))
    debug(`[AssessmentStorage] Saved attempt: ${attempt.id}`)
  } catch (error) {
    logError('Failed to save attempt:', error)
  }
}

export function getAttempt(id: string): AssessmentAttempt | null {
  try {
    const stored = localStorage.getItem(ATTEMPTS_KEY)
    if (!stored) return null
    const attempts: Record<string, AssessmentAttempt> = JSON.parse(stored)
    return attempts[id] || null
  } catch (error) {
    logError('Failed to load attempt:', error)
    return null
  }
}

export function getAllAttempts(): AssessmentAttempt[] {
  try {
    const stored = localStorage.getItem(ATTEMPTS_KEY)
    if (!stored) return []
    const attempts: Record<string, AssessmentAttempt> = JSON.parse(stored)
    return Object.values(attempts).sort((a, b) => b.startedAt - a.startedAt)
  } catch (error) {
    logError('Failed to load attempts:', error)
    return []
  }
}

export function getAttemptsByGroup(group: GroupId): AssessmentAttempt[] {
  return getAllAttempts().filter((a) => a.selectedGroup === group)
}

export function saveReport(report: AssessmentReport): void {
  try {
    const stored = localStorage.getItem(REPORTS_KEY)
    const reports: Record<string, AssessmentReport> = stored
      ? JSON.parse(stored)
      : {}
    reports[report.attemptId] = report
    safeStorageWrite(REPORTS_KEY, JSON.stringify(reports))
    debug(`[AssessmentStorage] Saved report: ${report.attemptId}`)
  } catch (error) {
    logError('Failed to save report:', error)
  }
}

export function getReport(attemptId: string): AssessmentReport | null {
  try {
    const stored = localStorage.getItem(REPORTS_KEY)
    if (!stored) return null
    const reports: Record<string, AssessmentReport> = JSON.parse(stored)
    return reports[attemptId] || null
  } catch (error) {
    logError('Failed to load report:', error)
    return null
  }
}

export function getAllReports(): AssessmentReport[] {
  try {
    const stored = localStorage.getItem(REPORTS_KEY)
    if (!stored) return []
    const reports: Record<string, AssessmentReport> = JSON.parse(stored)
    return Object.values(reports).sort((a, b) => b.completedAt - a.completedAt)
  } catch (error) {
    logError('Failed to load reports:', error)
    return []
  }
}

export function getReportsByGroup(group: GroupId): AssessmentReport[] {
  return getAllReports().filter((r) => r.selectedGroup === group)
}

export function getAssessmentHistory(
  group?: GroupId,
): AssessmentHistoryEntry[] {
  const attempts = group ? getAttemptsByGroup(group) : getAllAttempts()
  return attempts
    .filter((a) => a.completedAt !== null)
    .map((a) => {
      const correctCount = a.answers.filter((ans) => ans.isCorrect).length
      const percentage = Math.round((correctCount / a.answers.length) * 100)
      return {
        attemptId: a.id,
        blueprintId: a.blueprintId,
        completedAt: a.completedAt!,
        selectedGroup: a.selectedGroup,
        score: percentage,
        totalQuestions: a.answers.length,
        totalTimeSeconds: a.totalTimeSeconds,
      }
    })
    .sort((a, b) => b.completedAt - a.completedAt)
}

export function deleteAttempt(id: string): void {
  try {
    const attemptsStored = localStorage.getItem(ATTEMPTS_KEY)
    if (attemptsStored) {
      const attempts: Record<string, AssessmentAttempt> =
        JSON.parse(attemptsStored)
      delete attempts[id]
      safeStorageWrite(ATTEMPTS_KEY, JSON.stringify(attempts))
    }
    const reportsStored = localStorage.getItem(REPORTS_KEY)
    if (reportsStored) {
      const reports: Record<string, AssessmentReport> =
        JSON.parse(reportsStored)
      delete reports[id]
      safeStorageWrite(REPORTS_KEY, JSON.stringify(reports))
    }
    debug(`[AssessmentStorage] Deleted attempt and report: ${id}`)
  } catch (error) {
    logError('Failed to delete attempt:', error)
  }
}

export function clearAllAssessmentData(): void {
  try {
    localStorage.removeItem(BLUEPRINTS_KEY)
    localStorage.removeItem(ATTEMPTS_KEY)
    localStorage.removeItem(REPORTS_KEY)
    debug('[AssessmentStorage] Cleared all assessment data')
  } catch (error) {
    logError('Failed to clear assessment data:', error)
  }
}

/**
 * Get the timestamp of the last completed assessment for a specific group.
 * Returns 0 if no assessment has been completed for this group.
 *
 * RESOLVES: CONFLICT-14 (Assessment cooldown was global, now per-group)
 */
export function getLastAssessmentTimestamp(group: GroupId): number {
  const attempts = getAttemptsByGroup(group)
  const completed = attempts.filter(
    (a) => a.status === 'completed' && a.completedAt !== null,
  )
  if (completed.length === 0) return 0
  return Math.max(...completed.map((a) => a.completedAt!))
}

/**
 * Check if the assessment cooldown has elapsed for a specific group.
 * Returns an object with cooldown status and remaining time.
 *
 * RESOLVES: CONFLICT-14 (Assessment cooldown is now per-group)
 */
export function checkAssessmentCooldown(group: GroupId): {
  canStart: boolean
  remainingMs: number
  lastCompletedAt: number
} {
  const lastCompleted = getLastAssessmentTimestamp(group)
  if (lastCompleted === 0) {
    return { canStart: true, remainingMs: 0, lastCompletedAt: 0 }
  }

  const elapsed = Date.now() - lastCompleted
  const remaining = ASSESSMENT_COOLDOWN.MIN_INTERVAL_MS - elapsed

  return {
    canStart: remaining <= 0,
    remainingMs: Math.max(0, remaining),
    lastCompletedAt: lastCompleted,
  }
}
